import { useMemo, useState } from "react"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Button } from "@/components/Button"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import {
  usePendingRegistrations,
  type PendingRegistration,
} from "@/hooks/usePendingRegistrations"
import { useToast } from "@/components/Toast"
import { client, APIError } from "@/api"
import {
  EnvelopeIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

/**
 * Admin page: Pending Registrations.
 *
 * Surfaces the `registration_tokens` table so an admin can recover the
 * `/register/complete?token=...` URL for users who started the two-step
 * signup flow but never got (or lost) the email with that link.
 *
 * Primary use cases:
 *   - Local dev: email delivery is disabled, so every signup lands here
 *     and never progresses. Admin copies the URL into a browser tab to
 *     complete the flow.
 *   - Prod support: user signed up, email bounced or went to spam, they
 *     can't retry (`/v1/auth/request-registration` rejects duplicate
 *     emails). Admin hands them the URL out-of-band.
 *
 * Delete is offered for cleanup — removing the token lets the user
 * restart signup cleanly with the same email.
 */
export function PendingRegistrations() {
  const { toast } = useToast()
  const { data, isLoading, error, refetch } = usePendingRegistrations()
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PendingRegistration | null>(
    null
  )

  const rows = useMemo(() => {
    if (!data?.pendingRegistrations) return []
    // Backend already returns newest-first; no need to re-sort here.
    return data.pendingRegistrations
  }, [data])

  const handleCopy = async (row: PendingRegistration) => {
    try {
      await navigator.clipboard.writeText(row.completion_url)
      setCopiedId(row.id)
      setTimeout(() => {
        // Only clear if we're still showing this row as copied — a later
        // copy on a different row will have already swapped the id.
        setCopiedId((current) => (current === row.id ? null : current))
      }, 2000)
    } catch {
      toast({
        title: "Couldn't copy",
        description: "Select the URL manually and copy it.",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async (row: PendingRegistration) => {
    try {
      const response = await (client.admin as any)["pending-registrations"][
        ":id"
      ].$delete({
        param: { id: row.id.toString() },
      })

      if (!response.ok) {
        const error = (await response.json()) as any
        throw new APIError(
          response.status,
          error.code || "DELETE_PENDING_REGISTRATION_FAILED",
          new Error(error.message || "Failed to delete pending registration")
        )
      }

      toast({
        title: "Pending registration deleted",
        description: `${row.email} can restart signup with this email.`,
      })
      refetch()
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof APIError
            ? error.originalError.message
            : "Failed to delete. Please try again.",
        variant: "destructive",
      })
    }
  }

  const columns = useMemo<ColumnDef<PendingRegistration>[]>(
    () => [
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <span className="font-medium text-primary">{row.original.email}</span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {new Date(row.original.created_at).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "expires_at",
        header: "Expires",
        cell: ({ row }) => (
          <span
            className={`text-sm ${
              row.original.is_expired
                ? "text-destructive font-medium"
                : "text-secondary"
            }`}
          >
            {row.original.is_expired
              ? "Expired"
              : new Date(row.original.expires_at).toLocaleString()}
          </span>
        ),
      },
      {
        id: "copy",
        header: "",
        cell: ({ row }) => {
          const isCopied = copiedId === row.original.id
          return (
            <Button
              variant={isCopied ? "default" : "outline"}
              size="sm"
              disabled={row.original.is_expired}
              onClick={() => handleCopy(row.original)}
              title={
                row.original.is_expired
                  ? "This token is expired — delete it so the user can restart signup."
                  : "Copy the signup-completion URL to your clipboard."
              }
            >
              {isCopied ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy link
                </>
              )}
            </Button>
          )
        },
      },
      {
        id: "delete",
        header: "",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPendingDelete(row.original)}
            title="Delete this pending registration so the user can restart signup."
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    // copiedId intentionally a dep — re-renders the "Copied" affordance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [copiedId]
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">
              Pending Registrations
            </h1>
            <p className="text-secondary">
              Users who started the two-step signup flow but haven't clicked
              the email link yet. Copy the completion URL to hand to them
              out-of-band if the email never arrived.
            </p>
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<EnvelopeIcon className="h-12 w-12" />}
            title="No pending registrations"
            description="Every signup has been completed, expired, or cancelled."
          />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            showToolbar={false}
            showPagination={false}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
        title="Delete pending registration?"
        description={
          pendingDelete
            ? `Remove the pending signup for ${pendingDelete.email}. They'll be able to restart signup with this email.`
            : ""
        }
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={() => {
          if (pendingDelete) {
            handleDelete(pendingDelete)
            setPendingDelete(null)
          }
        }}
      />
    </Layout>
  )
}
